import React, { useState, useEffect } from 'react';
import { Calculator, RefreshCw, Info, DollarSign, Percent, Users, Building2, Save, Sliders } from 'lucide-react';
import { toast } from 'react-toastify';
import Api from '../Config/ApiConfig';

const PricingCalculator = () => {
  // Default values from the documentation
  const defaults = {
    ticketFare: 1000,
    packageType: 'premium',
    basicPercent: 33,
    mediumPercent: 40,
    premiumPercent: 50,
    carerShare: 90,
    adminShare: 10,
    promoType: 'none',
    promoRate: 0,
    isDomestic: false,
    cancellationWindow: 'none',
    miscAgentPercent: 45,
    miscReferralPercent: 10,
    b4tMiscChargesPercent: 35,
    agentSilverPercent: 65,
    agentGoldPercent: 75,
    agentPlatinumPercent: 100
  };

  const [values, setValues] = useState(defaults);
  const [activeTab, setActiveTab] = useState('calculator');

  // Dynamic system minimum pricing state
  const [minPricing, setMinPricing] = useState({ domestic: 35, international: 50 });
  const [minPricingInputs, setMinPricingInputs] = useState({ domestic: 35, international: 50 });
  const [loadingPricing, setLoadingPricing] = useState(false);
  const [savingPricing, setSavingPricing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchMinimumPricing = async () => {
    try {
      setLoadingPricing(true);
      const res = await Api.get('/minimum-pricing');
      if (res.data && res.data.data) {
        const data = res.data.data;
        const dom = Number(data.domestic) || 35;
        const intl = Number(data.international) || 50;
        setMinPricing({ domestic: dom, international: intl });
        setMinPricingInputs({ domestic: dom, international: intl });
        if (data.updatedAt) {
          setLastUpdated(new Date(data.updatedAt).toLocaleTimeString());
        }
      }
    } catch (err) {
      console.warn("Failed to load minimum pricing settings:", err);
    } finally {
      setLoadingPricing(false);
    }
  };

  useEffect(() => {
    fetchMinimumPricing();
  }, []);

  const handleSaveMinimumPricing = async (e) => {
    e?.preventDefault?.();
    const dom = Number(minPricingInputs.domestic);
    const intl = Number(minPricingInputs.international);
    if (!Number.isFinite(dom) || dom <= 0 || !Number.isFinite(intl) || intl <= 0) {
      toast.error("Please enter valid positive numbers for minimum pricing.");
      return;
    }
    try {
      setSavingPricing(true);
      const res = await Api.put('/minimum-pricing', { domestic: dom, international: intl });
      if (res.data) {
        setMinPricing({ domestic: dom, international: intl });
        setLastUpdated(new Date().toLocaleTimeString());
        toast.success(`Minimum pricing saved! Domestic: $${dom}, International: $${intl}`);
      }
    } catch (err) {
      console.error("Failed to save minimum pricing:", err);
      toast.error(err?.response?.data?.message || "Failed to update minimum pricing settings.");
    } finally {
      setSavingPricing(false);
    }
  };

  const updateValue = (key, value) => {
    setValues(prev => ({ ...prev, [key]: value }));
  };

  const resetToDefaults = () => {
    setValues(defaults);
  };

  // Calculations
  const getPackagePercent = () => {
    switch(values.packageType) {
      case 'basic': return values.basicPercent;
      case 'medium': return values.mediumPercent;
      case 'premium': return values.premiumPercent;
      default: return 0;
    }
  };

  const minimumRequired = values.isDomestic ? minPricing.domestic : minPricing.international;
  const packagePercent = getPackagePercent();
  const rawServiceFee = values.ticketFare * (packagePercent / 100);
  
  // Apply promo
  let discount = 0;
  let finalServiceFee = rawServiceFee;
  
  if (values.promoType === 'percentage') {
    discount = rawServiceFee * (values.promoRate / 100);
    finalServiceFee = rawServiceFee - discount;
  } else if (values.promoType === 'fixed') {
    discount = values.promoRate;
    finalServiceFee = rawServiceFee - discount;
  }

  const totalAmount = Math.max(finalServiceFee, minimumRequired);
  const subtotal = Math.round((totalAmount * values.carerShare) / 100);
  const b4tMiscCharges = Math.round((subtotal * values.b4tMiscChargesPercent) / 100 * 100) / 100;
  
  const agentAmount = Math.round((b4tMiscCharges * values.miscAgentPercent / 100) * 100) / 100;
  const referralAmount = Math.round((b4tMiscCharges * values.miscReferralPercent / 100) * 100) / 100;
  const internalAmount = b4tMiscCharges - agentAmount - referralAmount;

  const agentSilverAmount = Math.round((agentAmount * values.agentSilverPercent / 100) * 100) / 100;
  const agentGoldAmount = Math.round((agentAmount * values.agentGoldPercent / 100) * 100) / 100;
  const agentPlatinumAmount = Math.round((agentAmount * values.agentPlatinumPercent / 100) * 100) / 100;

  const netCarerAmount = subtotal - b4tMiscCharges;
  const platformFee = Math.round((totalAmount * values.adminShare) / 100);
  const adminCharges = Math.round(((totalAmount - platformFee) * 0.1) * 100) / 100;
  const b4tRetainedTotal = Math.round((platformFee + internalAmount + adminCharges) * 100) / 100;

  // Cancellation
  let cancellationCharge = 0;
  if (values.cancellationWindow === 'under24') {
    cancellationCharge = subtotal;
  } else if (values.cancellationWindow === '24to48') {
    cancellationCharge = subtotal * 0.5;
  }

  const refundAmount = subtotal - cancellationCharge;

  const netCarerAfterCancellation =
    values.cancellationWindow === 'none'
      ? netCarerAmount
      : Math.max(netCarerAmount - cancellationCharge, 0);
  const b4tRetainedAfterCancellation =
    values.cancellationWindow === 'none'
      ? b4tRetainedTotal
      : Math.round((b4tRetainedTotal + cancellationCharge) * 100) / 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 p-4 text-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-emerald-100">
          {/* Header */}
          <div className="bg-gradient-to-r from-emerald-600 to-teal-500 p-6 text-white">
            <div className="flex items-center gap-3 mb-2">
              <Calculator className="w-8 h-8" />
              <h1 className="text-3xl font-bold">B4T Pricing Calculator</h1>
            </div>
            <p className="text-emerald-100">Simulate and understand the complete money flow in Buddy4travel</p>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200">
            <div className="flex">
              <button
                onClick={() => setActiveTab('calculator')}
                className={`px-6 py-3 font-medium transition-colors ${
                  activeTab === 'calculator'
                    ? 'border-b-2 border-emerald-600 text-emerald-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Calculator
              </button>
              <button
                onClick={() => setActiveTab('details')}
                className={`px-6 py-3 font-medium transition-colors ${
                  activeTab === 'details'
                    ? 'border-b-2 border-emerald-600 text-emerald-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                System Details
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {activeTab === 'calculator' ? (
              <div className="grid lg:grid-cols-2 gap-6">
                {/* Input Panel */}
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-bold text-gray-900">Configuration</h2>
                    <button
                      onClick={resetToDefaults}
                      className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-sm font-medium"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Reset to Defaults
                    </button>
                  </div>

                  {/* System Minimum Pricing Floor Controls */}
                  <div className="bg-gradient-to-r from-emerald-50 via-teal-50 to-emerald-100 border border-emerald-300 p-4 rounded-xl shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Sliders className="w-4 h-4 text-emerald-700" />
                        <h3 className="text-sm font-bold text-emerald-950">System Minimum Fare Floor (Live Settings)</h3>
                      </div>
                      <div className="flex items-center gap-2">
                        {lastUpdated && (
                          <span className="text-xs text-gray-500 hidden sm:inline">Last updated: {lastUpdated}</span>
                        )}
                        <button
                          type="button"
                          onClick={fetchMinimumPricing}
                          title="Refresh current settings from server"
                          className="p-1 hover:bg-emerald-200/50 rounded transition-colors text-emerald-800"
                        >
                          <RefreshCw className={`w-3.5 h-3.5 ${loadingPricing ? 'animate-spin' : ''}`} />
                        </button>
                      </div>
                    </div>
                    <p className="text-xs text-emerald-800 mb-3">
                      Minimum fare enforced on all bookings. Changes saved here take effect immediately across the backend API and mobile apps.
                    </p>
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">
                          Domestic Min ($ USD)
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-2 text-gray-500 text-xs font-bold">$</span>
                          <input
                            type="number"
                            min="1"
                            step="1"
                            value={minPricingInputs.domestic}
                            onChange={(e) => setMinPricingInputs(prev => ({ ...prev, domestic: e.target.value }))}
                            className="w-full pl-6 pr-3 py-1.5 border border-emerald-300 rounded-lg text-sm text-gray-900 bg-white focus:ring-2 focus:ring-emerald-500"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">
                          International Min ($ USD)
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-2 text-gray-500 text-xs font-bold">$</span>
                          <input
                            type="number"
                            min="1"
                            step="1"
                            value={minPricingInputs.international}
                            onChange={(e) => setMinPricingInputs(prev => ({ ...prev, international: e.target.value }))}
                            className="w-full pl-6 pr-3 py-1.5 border border-emerald-300 rounded-lg text-sm text-gray-900 bg-white focus:ring-2 focus:ring-emerald-500"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between pt-1">
                      <span className="text-xs font-medium text-emerald-900">
                        Active: Domestic <span className="font-bold">${minPricing.domestic}</span> | International <span className="font-bold">${minPricing.international}</span>
                      </span>
                      <button
                        type="button"
                        onClick={handleSaveMinimumPricing}
                        disabled={savingPricing}
                        className="flex items-center gap-1 px-3 py-1.5 bg-emerald-700 hover:bg-emerald-800 disabled:opacity-50 text-white text-xs font-semibold rounded-lg shadow-sm transition-all"
                      >
                        <Save className="w-3.5 h-3.5" />
                        {savingPricing ? "Saving..." : "Save Pricing"}
                      </button>
                    </div>
                  </div>

                  {/* Ticket Fare */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Airline Ticket Fare (USD)
                    </label>
                    <input
                      type="number"
                      value={values.ticketFare}
                      onChange={(e) => updateValue('ticketFare', parseFloat(e.target.value) || 0)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-gray-900 placeholder-gray-400"
                    />
                  </div>

                  {/* Package Type */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Package Type
                    </label>
                    <select
                      value={values.packageType}
                      onChange={(e) => updateValue('packageType', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-gray-900"
                    >
                      <option value="basic">Basic ({values.basicPercent}%)</option>
                      <option value="medium">Medium ({values.mediumPercent}%)</option>
                      <option value="premium">Premium ({values.premiumPercent}%)</option>
                    </select>
                  </div>

                  {/* Commission Percentages */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-sm font-medium text-gray-700 mb-3">Package Commissions (%)</h3>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Basic</label>
                        <input
                          type="number"
                          value={values.basicPercent}
                          onChange={(e) => updateValue('basicPercent', parseFloat(e.target.value) || 0)}
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm text-gray-900 placeholder-gray-400"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Medium</label>
                        <input
                          type="number"
                          value={values.mediumPercent}
                          onChange={(e) => updateValue('mediumPercent', parseFloat(e.target.value) || 0)}
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm text-gray-900 placeholder-gray-400"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Premium</label>
                        <input
                          type="number"
                          value={values.premiumPercent}
                          onChange={(e) => updateValue('premiumPercent', parseFloat(e.target.value) || 0)}
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm text-gray-900 placeholder-gray-400"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Revenue Split */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-sm font-medium text-gray-700 mb-3">Revenue Split (%)</h3>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Carer Share</label>
                        <input
                          type="number"
                          value={values.carerShare}
                          onChange={(e) => updateValue('carerShare', parseFloat(e.target.value) || 0)}
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm text-gray-900 placeholder-gray-400"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Admin Share</label>
                        <input
                          type="number"
                          value={values.adminShare}
                          onChange={(e) => updateValue('adminShare', parseFloat(e.target.value) || 0)}
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm text-gray-900 placeholder-gray-400"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Misc Charges Split */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-sm font-medium text-gray-700 mb-3">Misc Charges Configuration</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Total B4T Misc Charges (%)</label>
                        <input
                          type="number"
                          value={values.b4tMiscChargesPercent}
                          onChange={(e) => updateValue('b4tMiscChargesPercent', parseFloat(e.target.value) || 0)}
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm text-gray-900 placeholder-gray-400"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Agents Share (%)</label>
                          <input
                            type="number"
                            value={values.miscAgentPercent}
                            onChange={(e) => updateValue('miscAgentPercent', parseFloat(e.target.value) || 0)}
                            className="w-full px-3 py-2 border border-gray-300 rounded text-sm text-gray-900 placeholder-gray-400"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Referral Share (%)</label>
                          <input
                            type="number"
                            value={values.miscReferralPercent}
                            onChange={(e) => updateValue('miscReferralPercent', parseFloat(e.target.value) || 0)}
                            className="w-full px-3 py-2 border border-gray-300 rounded text-sm text-gray-900 placeholder-gray-400"
                          />
                        </div>
                      </div>
                      <div>
                        <h4 className="text-xs font-semibold text-gray-700 mb-2">Agent Tiers (% of Agents Share)</h4>
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <label className="block text-[11px] text-gray-500 mb-1">Silver (%)</label>
                            <input
                              type="number"
                              value={values.agentSilverPercent}
                              onChange={(e) => updateValue('agentSilverPercent', parseFloat(e.target.value) || 0)}
                              className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs text-gray-900 placeholder-gray-400"
                            />
                          </div>
                          <div>
                            <label className="block text-[11px] text-gray-500 mb-1">Gold (%)</label>
                            <input
                              type="number"
                              value={values.agentGoldPercent}
                              onChange={(e) => updateValue('agentGoldPercent', parseFloat(e.target.value) || 0)}
                              className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs text-gray-900 placeholder-gray-400"
                            />
                          </div>
                          <div>
                            <label className="block text-[11px] text-gray-500 mb-1">Platinum (%)</label>
                            <input
                              type="number"
                              value={values.agentPlatinumPercent}
                              onChange={(e) => updateValue('agentPlatinumPercent', parseFloat(e.target.value) || 0)}
                              className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs text-gray-900 placeholder-gray-400"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Promo */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-sm font-medium text-gray-700 mb-3">Promotion</h3>
                    <select
                      value={values.promoType}
                      onChange={(e) => updateValue('promoType', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg mb-3 text-gray-900"
                    >
                      <option value="none">None</option>
                      <option value="percentage">Percentage Discount</option>
                      <option value="fixed">Fixed Amount Discount</option>
                    </select>
                    {values.promoType !== 'none' && (
                      <input
                        type="number"
                        value={values.promoRate}
                        onChange={(e) => updateValue('promoRate', parseFloat(e.target.value) || 0)}
                        placeholder={values.promoType === 'percentage' ? 'Percentage (%)' : 'Amount (USD)'}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400"
                      />
                    )}
                  </div>

                  {/* Route Type & Cancellation */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={values.isDomestic}
                          onChange={(e) => updateValue('isDomestic', e.target.checked)}
                          className="w-4 h-4 text-blue-600"
                        />
                        <span className="text-sm font-medium text-gray-700">Domestic Route</span>
                      </label>
                      <p className="text-xs text-gray-500 mt-1">Min: ${minimumRequired}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Cancellation
                      </label>
                      <select
                        value={values.cancellationWindow}
                        onChange={(e) => updateValue('cancellationWindow', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm text-gray-900"
                      >
                        <option value="none">None</option>
                        <option value="over48">&gt; 48h (0%)</option>
                        <option value="24to48">24-48h (50%)</option>
                        <option value="under24">&lt; 24h (100%)</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Results Panel */}
                <div className="space-y-4">
                  <h2 className="text-xl font-bold text-gray-900">Calculation Results</h2>

                  {/* Flow Chart */}
                  <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-6 rounded-lg border-2 border-emerald-200">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-3 bg-white rounded-lg shadow-sm">
                        <span className="text-sm font-medium text-gray-600">Airline Ticket Fare</span>
                        <span className="text-lg font-bold text-gray-900">${values.ticketFare.toFixed(2)}</span>
                      </div>

                      <div className="flex items-center justify-center">
                        <div className="text-center text-xs text-gray-500">
                          ↓ Package {packagePercent}% applied
                        </div>
                      </div>

                      <div className="flex items-center justify-between p-3 bg-white rounded-lg shadow-sm">
                        <span className="text-sm font-medium text-gray-600">Raw Service Fee</span>
                        <span className="text-lg font-bold text-emerald-600">${rawServiceFee.toFixed(2)}</span>
                      </div>

                      {discount > 0 && (
                        <>
                          <div className="flex items-center justify-center">
                            <div className="text-center text-xs text-green-600">
                              ↓ Discount -${discount.toFixed(2)}
                            </div>
                          </div>
                          <div className="flex items-center justify-between p-3 bg-white rounded-lg shadow-sm">
                            <span className="text-sm font-medium text-gray-600">After Discount</span>
                            <span className="text-lg font-bold text-green-600">${finalServiceFee.toFixed(2)}</span>
                          </div>
                        </>
                      )}

                      <div className="flex items-center justify-center">
                        <div className="text-center text-xs text-gray-500">
                          ↓ Min ${minimumRequired} applied
                        </div>
                      </div>

                      <div className="flex items-center justify-between p-4 bg-gradient-to-r from-emerald-600 to-teal-500 rounded-lg shadow-md">
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-5 h-5 text-white" />
                          <span className="text-sm font-medium text-white">Total Amount (Care-Seeker Pays)</span>
                        </div>
                        <span className="text-2xl font-bold text-white">${totalAmount.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Revenue Split */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <Percent className="w-4 h-4" />
                      <span>Revenue Distribution</span>
                      {values.cancellationWindow !== 'none' && (
                        <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-700">
                          Cancellation Applied
                        </span>
                      )}
                    </h3>
                    <div className="space-y-3">
                      <div className="bg-white rounded border-l-4 border-green-500 overflow-hidden">
                        <div className="flex items-center justify-between p-3 border-b border-gray-100">
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-green-600" />
                            <span className="text-sm font-medium text-gray-700">Carer Share ({values.carerShare}%)</span>
                          </div>
                          <span className="text-lg font-bold text-gray-400">${subtotal.toFixed(2)}</span>
                        </div>
                        <div className="bg-green-50 p-3 space-y-2">
                          <div className="flex justify-between text-sm">
                             <span className="text-red-600 pl-6">- B4T Misc Charges ({values.b4tMiscChargesPercent}%)</span>
                             <span className="font-medium text-red-600">-${b4tMiscCharges.toFixed(2)}</span>
                          </div>
                          
                          {/* Misc Charges Breakdown */}
                          <div className="pl-8 pr-2 space-y-1 text-xs text-gray-500 border-l-2 border-red-100 ml-6">
                            <div className="flex justify-between">
                              <span>Agents ({values.miscAgentPercent}%)</span>
                              <span>${agentAmount.toFixed(2)}</span>
                            </div>
                            <div className="pl-4 space-y-1">
                              <div className="flex justify-between">
                                <span>Silver ({values.agentSilverPercent}%)</span>
                                <span>${agentSilverAmount.toFixed(2)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Gold ({values.agentGoldPercent}%)</span>
                                <span>${agentGoldAmount.toFixed(2)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Platinum ({values.agentPlatinumPercent}%)</span>
                                <span>${agentPlatinumAmount.toFixed(2)}</span>
                              </div>
                            </div>
                            <div className="flex justify-between">
                              <span>Referral Rewards ({values.miscReferralPercent}%)</span>
                              <span>${referralAmount.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Internal (Rest {100 - values.miscAgentPercent - values.miscReferralPercent}%)</span>
                              <span>${internalAmount.toFixed(2)}</span>
                            </div>
                          </div>

                          <div className="flex justify-between text-sm border-t border-green-200 pt-2">
                             <span className="font-bold text-green-800 pl-6">
                               {values.cancellationWindow === 'none'
                                 ? `Net Carer Receives (${100 - values.b4tMiscChargesPercent}%)`
                                 : `Net Carer Receives (${100 - values.b4tMiscChargesPercent}% - After Cancellation)`}
                             </span>
                             <span className="font-bold text-green-700">
                               $
                               {(values.cancellationWindow === 'none'
                                 ? netCarerAmount
                                 : netCarerAfterCancellation
                               ).toFixed(2)}
                             </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-white rounded border-l-4 border-emerald-500">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-emerald-600" />
                          <span className="text-sm font-medium text-gray-700">Platform Fee ({values.adminShare}%)</span>
                        </div>
                        <span className="text-lg font-bold text-emerald-600">${platformFee.toFixed(2)}</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-white rounded border-l-4 border-gray-400">
                        <span className="text-sm font-medium text-gray-600">Admin Charges (Internal 10%)</span>
                        <span className="text-sm font-semibold text-gray-700">${adminCharges.toFixed(2)}</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-white rounded border-l-4 border-blue-400">
                        <span className="text-sm font-medium text-gray-700">
                          {values.cancellationWindow === 'none'
                            ? 'Total B4T Retained (Platform + Internal)'
                            : 'Total B4T Retained (After Cancellation)'}
                        </span>
                        <span className="text-lg font-bold text-blue-700">
                          $
                          {(values.cancellationWindow === 'none'
                            ? b4tRetainedTotal
                            : b4tRetainedAfterCancellation
                          ).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Cancellation Info */}
                  {values.cancellationWindow !== 'none' && (
                    <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                      <h3 className="text-sm font-semibold text-red-800 mb-3">Cancellation Impact</h3>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-700">Cancellation Charge:</span>
                          <span className="font-bold text-red-600">${cancellationCharge.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-700">Refund Amount:</span>
                          <span className="font-bold text-green-600">${refundAmount.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-700">Net Carer After Cancellation:</span>
                          <span className="font-bold text-gray-900">${netCarerAfterCancellation.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-700">B4T Retained After Cancellation:</span>
                          <span className="font-bold text-blue-700">${b4tRetainedAfterCancellation.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="prose max-w-none text-gray-900">
                <div className="bg-emerald-50 border-l-4 border-emerald-500 p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-emerald-600 mt-0.5" />
                    <div>
                      <h3 className="text-lg font-semibold text-emerald-900 mt-0 mb-2">How B4T Pricing Works</h3>
                      <p className="text-sm text-emerald-800 mb-0">This system calculates service fees based on airline ticket prices, applies package-based percentages, and distributes revenue between carers and the platform.</p>
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-white border border-gray-200 rounded-lg p-5">
                    <h3 className="text-lg font-bold text-gray-900 mb-3">Commission Percentages (Active)</h3>
                    <ul className="space-y-2 text-sm">
                      <li><strong>Basic Package:</strong> 33% of ticket fare</li>
                      <li><strong>Medium Package:</strong> 40% of ticket fare</li>
                      <li><strong>Premium Package:</strong> 50% of ticket fare</li>
                      <li><strong>Carer Share:</strong> 90% of service fee</li>
                      <li><strong>Admin Share:</strong> 10% of service fee</li>
                    </ul>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-lg p-5">
                    <h3 className="text-lg font-bold text-gray-900 mb-3">Minimum Pricing Rules (Live)</h3>
                    <ul className="space-y-2 text-sm">
                      <li><strong>Domestic Routes:</strong> ${minPricing.domestic} USD minimum</li>
                      <li><strong>International Routes:</strong> ${minPricing.international} USD minimum</li>
                      <li className="text-gray-600 italic">The final amount charged is the maximum of the calculated service fee and the applicable minimum. These values can be updated directly from the Calculator tab.</li>
                    </ul>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-lg p-5">
                    <h3 className="text-lg font-bold text-gray-900 mb-3">Cancellation Policy</h3>
                    <ul className="space-y-2 text-sm">
                      <li><strong>&gt; 48 hours:</strong> 0% charge (full refund)</li>
                      <li><strong>24-48 hours:</strong> 50% of subtotal charged</li>
                      <li><strong>&lt; 24 hours:</strong> 100% of subtotal charged</li>
                      <li className="text-gray-600 italic">Cancellation charges are based on the carer's subtotal amount.</li>
                    </ul>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-lg p-5">
                    <h3 className="text-lg font-bold text-gray-900 mb-3">Promotion Types</h3>
                    <ul className="space-y-2 text-sm">
                      <li><strong>Percentage:</strong> X% off the service fee</li>
                      <li><strong>Fixed Amount:</strong> Fixed dollar discount</li>
                      <li><strong>Fixed Price:</strong> Set total to specific amount</li>
                      <li className="text-gray-600 italic">Discounts are applied after package percentage calculation.</li>
                    </ul>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-lg p-6 mt-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Calculation Flow</h3>
                  <ol className="space-y-3 text-sm">
                    <li className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-emerald-600 text-white rounded-full flex items-center justify-center text-xs font-bold">1</span>
                      <span><strong>Base Fare:</strong> Get airline ticket price from Amadeus API</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-emerald-600 text-white rounded-full flex items-center justify-center text-xs font-bold">2</span>
                      <span><strong>Apply Package %:</strong> Calculate raw service fee = Fare × Package% (33/40/50%)</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-emerald-600 text-white rounded-full flex items-center justify-center text-xs font-bold">3</span>
                      <span><strong>Apply Promo:</strong> Subtract discount if promotion code is valid</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-emerald-600 text-white rounded-full flex items-center justify-center text-xs font-bold">4</span>
                      <span><strong>Check Minimum:</strong> Ensure total meets minimum (${minPricing.domestic} domestic / ${minPricing.international} international)</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-emerald-600 text-white rounded-full flex items-center justify-center text-xs font-bold">5</span>
                      <span><strong>Split Revenue:</strong> Carer gets 90%, Platform gets 10% of the total amount</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-emerald-600 text-white rounded-full flex items-center justify-center text-xs font-bold">6</span>
                      <span><strong>Process Payment:</strong> Charge care-seeker via Stripe, credit carer wallet upon completion</span>
                    </li>
                  </ol>
                </div>

                <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mt-6">
                  <h4 className="text-sm font-semibold text-yellow-900 mb-2">Important Notes</h4>
                  <ul className="text-sm text-yellow-800 space-y-1 mb-0">
                    <li>• The airline ticket itself is NOT purchased through this platform</li>
                    <li>• Service fees are for the care companion service during travel</li>
                    <li>• Carer receives full subtotal amount when trip is completed</li>
                    <li>• Admin charges (10% internal) are for reporting; don't affect carer payout</li>
                    <li>• Stripe processing fees are separate and not modeled in these calculations</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingCalculator;
